/**
 * Dashboard metrics — single endpoint, polled every 30s while the tab is
 * visible. The slower cadence vs `useMessagesList` (5s) is deliberate:
 * aggregate counts move much more slowly than individual message status
 * transitions, and a 30s poll keeps the dashboard snappy without saturating
 * the API for users who leave it open in a background tab.
 *
 * Optional `range` scopes the delivery-rate, status, volume and period-volume
 * metrics to a window (plus the preceding equal window for deltas). Omitted →
 * the endpoint keeps its legacy fixed windows, so the default dashboard is
 * byte-for-byte unchanged.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface StatusBucket {
  status: string;
  count: number;
}

export interface VolumePoint {
  /** ISO date (YYYY-MM-DD) */
  day: string;
  count: number;
}

export interface SpendPoint {
  /** ISO date (YYYY-MM-DD) */
  day: string;
  /** Net SMS spend for the day (USD). */
  amount: number;
}

export interface StatsOverview {
  sent_today: number;
  delivered_count: number;
  terminal_count: number;
  total_last_7d: number;
  /** null when there's no terminal data yet — render as `—`, not "0%". */
  delivery_rate: number | null;
  /** Fractional change in delivery rate vs the preceding equal window. */
  delivery_rate_delta: number | null;
  /** Fractional change in period volume vs the preceding equal window. */
  period_volume_delta: number | null;
  balance: number;
  credit_limit: number;
  /** Per-tenant low-balance threshold in USD. The dashboard banner
   *  uses this directly so the warning matches the customer's
   *  configured floor instead of a hardcoded constant. Defaults to
   *  10 (the InitTenantBalance default) for fresh tenants. */
  low_balance_threshold: number;
  /** Estimated days of runway at the current burn rate; null when there's
   *  no measurable burn. */
  runway_days: number | null;
  status_counts: StatusBucket[];
  volume: VolumePoint[];
  /** Last 14 days of net SMS burn for the balance sparkline. */
  spend_series: SpendPoint[];
}

export interface StatsRange {
  /** RFC3339 inclusive lower bound. */
  from: string;
  /** RFC3339 exclusive upper bound. */
  to: string;
}

const STATS_KEY = ['stats', 'overview'] as const;

interface Options {
  enabled?: boolean;
  /** When set, scopes the metrics to [from, to). */
  range?: StatsRange;
}

export function useStatsOverview({ enabled = true, range }: Options = {}) {
  return useQuery({
    queryKey: [...STATS_KEY, range?.from ?? null, range?.to ?? null] as const,
    queryFn: () => {
      const path = range
        ? `/v1/stats/overview?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
        : '/v1/stats/overview';
      return api.get<StatsOverview>(path);
    },
    enabled,
    refetchInterval: enabled ? 30_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
}
